import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePrepTime } from "@/lib/settings";
import { Check, MapPin, Phone, Clock, Loader2, AlertTriangle } from "lucide-react";

export default function Confirmation() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  // Paiement en ligne : states
  const [payStatus, setPayStatus] = useState(sessionId ? "checking" : "n/a"); // checking | paid | pending | expired | error
  const pollRef = useRef(null);
  const prepTime = usePrepTime();

  const fetchOrder = () =>
    api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => {});

  useEffect(() => {
    fetchOrder().finally(() => setLoading(false));
  }, [id]);

  // Polling Stripe checkout status when returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const maxAttempts = 15;
    const interval = 2000;

    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPayStatus("paid");
          fetchOrder();
          return;
        }
        if (data.status === "expired") {
          setPayStatus("expired");
          return;
        }
        if (attempts >= maxAttempts) {
          setPayStatus("timeout");
          return;
        }
        pollRef.current = setTimeout(poll, interval);
      } catch {
        if (attempts >= maxAttempts) {
          setPayStatus("error");
        } else {
          pollRef.current = setTimeout(poll, interval);
        }
      }
    };
    poll();
    return () => clearTimeout(pollRef.current);
  }, [sessionId]);

  if (loading) return <div className="pt-32 text-center text-muted-foreground">Chargement…</div>;
  if (!order) return <div className="pt-32 text-center" data-testid="order-not-found">Commande introuvable.</div>;

  const isOnline = order.payment_method === "online";
  const orderPaid = order.payment_status === "paid";
  const showPaymentBanner = isOnline && sessionId;

  return (
    <div data-testid="confirmation-page" className="pt-28 pb-20 min-h-[80vh] bg-background">
      <div className="max-w-2xl mx-auto px-6">
        {showPaymentBanner && payStatus === "checking" && (
          <div data-testid="pay-checking" className="mb-8 p-5 bg-amber-50 border border-amber-300 rounded-sm flex items-start gap-3">
            <Loader2 className="animate-spin text-amber-700 shrink-0 mt-0.5" size={22} />
            <div>
              <p className="font-medium text-amber-900">Vérification du paiement…</p>
              <p className="text-sm text-amber-800/80 mt-1">Merci de patienter quelques secondes.</p>
            </div>
          </div>
        )}
        {showPaymentBanner && payStatus === "expired" && (
          <div data-testid="pay-expired" className="mb-8 p-5 bg-destructive/10 border border-destructive rounded-sm flex items-start gap-3">
            <AlertTriangle className="text-destructive shrink-0 mt-0.5" size={22} />
            <div>
              <p className="font-medium text-destructive">Session de paiement expirée</p>
              <p className="text-sm text-foreground mt-1">Aucun montant n&apos;a été débité. Vous pouvez repasser commande.</p>
            </div>
          </div>
        )}
        {showPaymentBanner && (payStatus === "timeout" || payStatus === "error") && (
          <div data-testid="pay-error" className="mb-8 p-5 bg-amber-50 border border-amber-300 rounded-sm">
            <p className="font-medium text-amber-900">Vérification en cours…</p>
            <p className="text-sm text-amber-800/80 mt-1">Si votre paiement est confirmé côté banque, votre commande apparaîtra dans quelques instants. Rechargez la page ou appelez-nous si besoin.</p>
          </div>
        )}

        <div className="text-center mb-12 fade-up">
          <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-6 ${orderPaid || !isOnline ? "bg-primary text-white" : "bg-amber-100 text-amber-800"}`}>
            <Check size={32} />
          </div>
          <p className="uppercase tracking-[0.3em] text-xs text-primary mb-3">
            {isOnline ? (orderPaid ? "Paiement reçu · Commande confirmée" : "Commande en attente de paiement") : "Commande confirmée"}
          </p>
          <h1 className="font-display text-5xl text-foreground mb-3">Merci {order.customer.firstname} !</h1>
          <p className="text-muted-foreground">Votre commande <span className="font-semibold text-foreground" data-testid="order-number">#{order.order_number}</span>{orderPaid || !isOnline ? " est en cours de préparation." : " sera envoyée en cuisine dès validation du paiement."}</p>
        </div>

        <div className="bg-white border border-border rounded-sm p-6 mb-8 fade-up delay-200">
          <h2 className="font-display text-2xl mb-4">Détails</h2>
          <div className="space-y-3 mb-6">
            {order.items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span>{it.quantity}× {it.name}{it.addons?.length ? ` + ${it.addons.map(a => a.name).join(", ")}` : ""}</span>
                <span className="font-medium">{it.line_total.toFixed(2)} CHF</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-4 space-y-2 text-sm">
            <Row label="Sous-total" value={`${order.subtotal.toFixed(2)} CHF`} />
            {order.discount > 0 && <Row label="Réduction" value={`-${order.discount.toFixed(2)} CHF`} className="text-primary" />}
            <Row label={`TVA incluse (${order.tva_rate}%)`} value={`${order.tva_amount.toFixed(2)} CHF`} muted />
            <div className="border-t border-border pt-3 flex justify-between text-lg font-semibold">
              <span>Total</span>
              <span className="text-primary" data-testid="confirmation-total">{order.total.toFixed(2)} CHF</span>
            </div>
          </div>
        </div>

        <div className="bg-primary text-white rounded-sm p-6 mb-6 fade-up delay-300">
          <h3 className="font-display text-2xl mb-4">Retrait</h3>
          <div className="space-y-3 text-sm text-white/90">
            <div className="flex gap-3"><MapPin size={18} className="shrink-0" /><span>Rue de la Gare 8, 1110 Morges</span></div>
            <div className="flex gap-3"><Phone size={18} className="shrink-0" /><a href="tel:0218020593" className="hover:underline">021 802 05 93</a></div>
            <div className="flex gap-3"><Clock size={18} className="shrink-0" /><span>Retrait : {order.pickup_time === "asap" ? `dès que possible (~ ${prepTime} min)` : order.pickup_time}</span></div>
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground mb-8">
          {isOnline ? (
            <><strong>Paiement en ligne{orderPaid ? " confirmé" : ""}</strong> — {orderPaid ? "aucun paiement supplémentaire au retrait." : "vérification en cours."}</>
          ) : (
            <><strong>Paiement sur place au retrait</strong> — Twint, espèces ou carte bancaire.</>
          )}
        </p>

        <div className="text-center space-y-3">
          <Link to={`/suivi/${order.id}`} data-testid="track-order-link" className="inline-block bg-primary text-white px-6 py-3 rounded-sm hover:bg-primary/90 transition">Suivre ma commande →</Link>
          <div><Link to="/" className="text-primary hover:underline text-sm">Retour à l&apos;accueil</Link></div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, muted, className = "" }) {
  return <div className={`flex justify-between ${muted ? "text-muted-foreground" : ""} ${className}`}><span>{label}</span><span>{value}</span></div>;
}
